import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
import { IGeofenceAPI } from '@/common/models/geo/Geofence';
import { INetworkFrequencyCapAPI } from '@/common/models/targeting-criteria/NetworkFrequencyCap';
import { DateTime } from 'luxon';

export interface ICoreTargetingAPI {
  co2e_emissions?: {
    ad_selection_gco2e: number;
    creative_delivery_gco2e: number;
    inventorytargeting_id: number;
    media_distribution_gco2e: number;
    score: number;
    total_gco2e: number;
  };
  co2e_emissions_score_threshold?: number;
  unique_advertiser_id_to_check?: number;
  creative_format_exclusions: string[];
  creative_formats: string[];
  creative_orientation_exclusions: CreativeOrientationType[];
  creative_orientations: CreativeOrientationType[];
  iab_categories: string[];
  iab_category_exclusions: string[];
  geofencegroup_exclusion_ids: number[];
  geofencegroup_ids: number[];
  lineitem_id?: number;
  language_exclusion_ids: number[];
  language_ids: number[];
  location_exclusion_ids: number[];
  location_ids: number[];
  market_exclusion_ids: number[];
  market_ids: number[];
  mediatype_exclusion_ids: number[];
  mediatype_ids: number[];
  network_exclusion_ids: number[];
  network_ids: number[];
  owner_ids: number[];
  organization_id?: number;
  package_id?: number;
  purchase_type?: string;
  site_exclusion_ids: number[];
  site_ids: number[];
  unit_exclusion_ids: number[];
  unit_ids: number[];
  unit_tag_exclusions: string[];
  unit_tags: string[];
  unitpack_exclusion_ids: number[];
  unitpack_ids: number[];
  buy_from_deals_enabled?: boolean;
  requested_for_utc?: DateTime;
}

export interface IInventoryTargetingAPI extends ICoreTargetingAPI {
  inventorytargeting_id: number;
}

export interface ILineItemTargetingAPI extends ICoreTargetingAPI {
  ad_duration: number;
  daypart: string;
  demographic_ids: number[];
  from_date: string;
  lineitem_networkfreqcaps: INetworkFrequencyCapAPI[];
  max_bid_cpm: number;
  owner_organization_ids: number[];
  package_exchange_id?: number;
  package_organization_id?: number;
  screen_frequency_cap: number;
  to_date: string;
  package_ids?: number[];
  packagegroup_id?: number;
  min_concentration_value?: number;
  lineitem_exchange_fee_override?: {
    type: string;
    value: number;
  };
  min_bid_cpm: number;
}

interface ILineItemCPMEntityFee {
  fee_category: { name: string; fee_category_id: number; payer: string };
  type: 'FIXED' | 'PERCENT';
  value: number;
}
export interface ILineItemCPMTargetingAPI extends ILineItemTargetingAPI {
  buyer_organization_dsp_id?: number;
  buyer_organization_id: number;
  measurement_requested: boolean;
  min_concentration_factor: number;
  unique_advertiser_id_to_check: number | null;
  targetingcriterion_ids?: number[];
  transient_entity_fees?: { organization_id: number; entity_type: 'LINEITEM'; fees: ILineItemCPMEntityFee[] };
  lineitem_features: string[];
  min_bid_cpm: number;
  campaign_id?: number;
}

export interface IEstimatedDeliveryStatsAPI {
  daypart?: string;
  demographic_ids?: number[];
  screen_frequency_cap?: number;
  max_bid_cpm?: number;
  cpm_fixed?: number;
  from_date: string;
  to_date: string;
  organization_id: number;
  package_ids?: number[];
  unit_targeting: ICoreTargetingAPI;
  network_frequency_caps?: INetworkFrequencyCapAPI[];
  targetingcriterion_ids?: number[];
  min_bid_cpm?: number;
  ad_duration?: number;
  geofences?: IGeofenceAPI[];
}

export default class InventoryTargeting {
  id: number;
  creativeFormatExclusions: string[];
  creativeFormats: string[];
  creativeOrientationExclusions: CreativeOrientationType[];
  creativeOrientations: CreativeOrientationType[];
  geofenceGroupExclusionIDs: number[];
  geofenceGroupIDs: number[];
  iabCategories: string[];
  iabCategoryExclusions: string[];
  languageExclusionIDs: number[];
  languageIDs: number[];
  locationExclusionIDs: number[];
  locationIDs: number[];
  marketExclusionIDs: number[];
  marketIDs: number[];
  mediaTypeExclusionIDs: number[];
  mediaTypeIDs: number[];
  networkExclusionIDs: number[];
  networkIDs: number[];
  ownerIDs: number[];
  siteExclusionIDs: number[];
  siteIDs: number[];
  unitExclusionIDs: number[];
  unitIDs: number[];
  unitTagExclusions: string[];
  unitTags: string[];
  unitPackExclusionIDs: number[];
  unitPackIDs: number[];

  constructor(
    id = -1,
    creativeFormatExclusions: string[] = [],
    creativeFormats: string[] = [],
    creativeOrientationExclusions: CreativeOrientationType[] = [],
    creativeOrientations: CreativeOrientationType[] = [],
    geofenceGroupExclusionIDs: number[] = [],
    geofenceGroupIDs: number[] = [],
    iabCategories: string[] = [],
    iabCategoryExclusions: string[] = [],
    languageExclusionIDs: number[] = [],
    languageIDs: number[] = [],
    locationExclusionIDs: number[] = [],
    locationIDs: number[] = [],
    marketExclusionIDs: number[] = [],
    marketIDs: number[] = [],
    mediaTypeExclusionIDs: number[] = [],
    mediaTypeIDs: number[] = [],
    networkExclusionIDs: number[] = [],
    networkIDs: number[] = [],
    ownerIDs: number[] = [],
    siteExclusionIDs: number[] = [],
    siteIDs: number[] = [],
    unitExclusionIDs: number[] = [],
    unitIDs: number[] = [],
    unitTagExclusions: string[] = [],
    unitTags: string[] = [],
    unitPackExclusionIDs: number[] = [],
    unitPackIDs: number[] = [],
  ) {
    this.id = id;
    this.creativeFormatExclusions = creativeFormatExclusions;
    this.creativeFormats = creativeFormats;
    this.creativeOrientationExclusions = creativeOrientationExclusions;
    this.creativeOrientations = creativeOrientations;
    this.geofenceGroupExclusionIDs = geofenceGroupExclusionIDs;
    this.geofenceGroupIDs = geofenceGroupIDs;
    this.iabCategories = iabCategories;
    this.iabCategoryExclusions = iabCategoryExclusions;
    this.languageExclusionIDs = languageExclusionIDs;
    this.languageIDs = languageIDs;
    this.locationExclusionIDs = locationExclusionIDs;
    this.locationIDs = locationIDs;
    this.marketExclusionIDs = marketExclusionIDs;
    this.marketIDs = marketIDs;
    this.mediaTypeExclusionIDs = mediaTypeExclusionIDs;
    this.mediaTypeIDs = mediaTypeIDs;
    this.networkExclusionIDs = networkExclusionIDs;
    this.networkIDs = networkIDs;
    this.ownerIDs = ownerIDs;
    this.siteExclusionIDs = siteExclusionIDs;
    this.siteIDs = siteIDs;
    this.unitExclusionIDs = unitExclusionIDs;
    this.unitIDs = unitIDs;
    this.unitTagExclusions = unitTagExclusions;
    this.unitTags = unitTags;
    this.unitPackExclusionIDs = unitPackExclusionIDs;
    this.unitPackIDs = unitPackIDs;
  }

  static fromAPI(apiObject: IInventoryTargetingAPI): InventoryTargeting {
    return new InventoryTargeting(
      apiObject.inventorytargeting_id,
      apiObject.creative_format_exclusions,
      apiObject.creative_formats,
      apiObject.creative_orientation_exclusions,
      apiObject.creative_orientations,
      apiObject.geofencegroup_exclusion_ids,
      apiObject.geofencegroup_ids,
      apiObject.iab_categories,
      apiObject.iab_category_exclusions,
      apiObject.language_exclusion_ids,
      apiObject.language_ids,
      apiObject.location_exclusion_ids,
      apiObject.location_ids,
      apiObject.market_exclusion_ids,
      apiObject.market_ids,
      apiObject.mediatype_exclusion_ids,
      apiObject.mediatype_ids,
      apiObject.network_exclusion_ids,
      apiObject.network_ids,
      apiObject.owner_ids,
      apiObject.site_exclusion_ids,
      apiObject.site_ids,
      apiObject.unit_exclusion_ids,
      apiObject.unit_ids,
      apiObject.unit_tag_exclusions,
      apiObject.unit_tags,
      apiObject.unitpack_exclusion_ids,
      apiObject.unitpack_ids,
    );
  }

  toApi(): IInventoryTargetingAPI {
    return {
      inventorytargeting_id: this.id >= 0 ? this.id : undefined,
      creative_format_exclusions: this.creativeFormatExclusions,
      creative_formats: this.creativeFormats,
      creative_orientation_exclusions: this.creativeOrientationExclusions,
      creative_orientations: this.creativeOrientations,
      geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
      geofencegroup_ids: this.geofenceGroupIDs,
      iab_categories: this.iabCategories,
      iab_category_exclusions: this.iabCategoryExclusions,
      language_exclusion_ids: this.languageExclusionIDs,
      language_ids: this.languageIDs,
      location_exclusion_ids: this.locationExclusionIDs,
      location_ids: this.locationIDs,
      market_exclusion_ids: this.marketExclusionIDs,
      market_ids: this.marketIDs,
      mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
      mediatype_ids: this.mediaTypeIDs,
      network_exclusion_ids: this.networkExclusionIDs,
      network_ids: this.networkIDs,
      owner_ids: this.ownerIDs,
      site_exclusion_ids: this.siteExclusionIDs,
      site_ids: this.siteIDs,
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_ids: this.unitIDs,
      unit_tag_exclusions: this.unitTagExclusions,
      unit_tags: this.unitTags,
      unitpack_exclusion_ids: this.unitPackExclusionIDs,
      unitpack_ids: this.unitPackIDs,
    };
  }

  copy(): InventoryTargeting {
    return InventoryTargeting.fromAPI(this.toApi());
  }
}
